import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useForm, useWatch } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  useCreateOperatorMutation,
  useOrganizationsShortQuery,
  usePartnerShortQuery,
} from '@common/cache';
import { createOperatorSchema } from '../schemas';
import { omit } from 'lodash';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { fillAddressFormValues } from '@util/google-places';
import { checkFormIsDirty } from '@util/checkFormIsDirty.ts';

interface ICreateOperatorFormShape {
  name: string;
  organization_id?: string;
  partner_id: string;
  street: string;
  suite?: string;
  city: string;
  state: string;
  county?: string;
  zip_code: string;
  google_places_id?: string | null;
}

const initialValues: Partial<ICreateOperatorFormShape> = {
  name: '',
  organization_id: '',
  partner_id: '',
  google_places_id: '',
  street: '',
  suite: '',
  city: '',
  state: '',
  county: '',
  zip_code: '',
};

export const useCreateOperator = (
  handleOpenOperatorDetails: (id: string) => void,
) => {
  const { mutateAsync, isPending } = useCreateOperatorMutation();
  const [isOpen, setIsOpen] = useState(false);
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);
  const { data: organizationOptions = [] } = useOrganizationsShortQuery();

  const formMethods = useForm<ICreateOperatorFormShape>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<ICreateOperatorFormShape, any, any>(
      createOperatorSchema,
    ),
    defaultValues: {
      ...initialValues,
      organization_id: organizationOptions[0]?.id,
    },
  });

  const {
    handleSubmit,
    setValue,
    reset,
    getValues,
    formState: { isSubmitting, isValid, dirtyFields },
  } = formMethods;

  const isFormDirty = checkFormIsDirty(dirtyFields);

  const org_id = useWatch({
    control: formMethods.control,
    name: 'organization_id',
  });

  const { data: partnerOptions = [], refetch } = usePartnerShortQuery(
    org_id as string,
  );

  useEffect(() => {
    if (org_id) {
      refetch();
    }
  }, [org_id, isOpen]);

  const handleClose = () => {
    if (isFormDirty) {
      return setIsSaveChangesModalOpen(true);
    }
    setIsOpen(false);
    reset();
  };

  const handleOnClick = () => {
    setIsOpen(true);
  };

  const handleSelectLocation = (place: Partial<google.maps.places.Place>) => {
    fillAddressFormValues({ setValue, place });
  };

  const submitHandler = async (values: ICreateOperatorFormShape) => {
    try {
      const response = await mutateAsync({
        ...omit(values, ['organization_id']),
        country: 'US',
        record_id: values.partner_id,
      });

      if (response.data.id) {
        handleOpenOperatorDetails(response.data.id);
      }

      setIsOpen(false);
      reset();
      toast.success('You added a new operator');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    setIsOpen(false);
    reset();
  };

  const handleSaveChanges = async () => {
    const values = getValues();

    try {
      await mutateAsync({
        ...omit(values, ['organization_id']),
        country: 'US',
        record_id: values.partner_id,
      });

      toast.success('You added a new operator');
      setIsSaveChangesModalOpen(false);
      setIsOpen(false);
      reset();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  return {
    organizationOptions: organizationOptions.map(o => ({
      value: o.id,
      label: o.name,
    })),
    partnerOptions: partnerOptions.map(o => ({
      value: o.id,
      label: o.name,
    })),
    onSubmit,
    isSubmitting,
    isOpen,
    submitHandler,
    handleSubmit,
    handleClose,
    handleOnClick,
    setValue,
    isPartnerSelectDisabled: organizationOptions.length > 0 && !org_id,
    handleSelectLocation,
    formMethods,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleSaveChanges,
    handleDiscardChanges,
    isValid,
    isEditSubmitting: isPending,
  };
};
